/*
 * file-converter/main.js — the "converter of everything" orchestrator.
 *
 * One drop zone, any file: detect (file-doctor signatures) → offer every
 * output our measured engines can actually produce → convert in-browser →
 * hand back files / one ZIP / straight into a folder.
 *
 * Design decisions that came from production telemetry, not taste:
 *  - Mixed batches are the norm, not the edge case (heic-converter: 1 344
 *    sessions produced 10 870 files in 30 days). The queue therefore groups
 *    per FAMILY, and "convert all to X" exists per family, not globally.
 *  - ZIP is streamed (zip-out.js) because JSZip's single-ArrayBuffer build
 *    is the #1 heic-converter error («Array buffer allocation failed»).
 *  - Jobs run sequentially. FFmpeg-WASM is single-threaded anyway, and the
 *    parallel-images experiment is not worth racing the memory guard on
 *    2 GB phones. Engine pressure relief between jobs via maybeReinit().
 *  - A file we cannot convert is not a dead end: broken media routes to
 *    /file-doctor, scanned PDFs to the OCR tool, locked PDFs to /unlock-pdf,
 *    and everything else gets the "suggest a format" form — that form is the
 *    roadmap generator for this tool.
 *
 * Billing: one daily-cap unit per FILE (repeat of the same file is free —
 * __toolLimit fileKey semantics). begin()/commit()/abort() wrap each file so
 * a cancelled or failed conversion never burns a slot.
 */

import { detectFile, isRaw } from './detect.js';
import { targetsFor, defaultTargetId, hintFor } from './matrix.js';
import { pack, saveToFolder, ZIP_HARD_LIMIT } from './zip-out.js';
import { jsonKind, evidenceOf } from './suggest-evidence.js';

const SLUG = 'file-converter';
const T = window.__FC_I18N || {};
const CFG = window.__FC_CFG || {};

const t = (k, repl) => {
    let s = T[k] || k;
    /* EVERY occurrence, not the first. `String.replace` with a string pattern
     * substitutes once, so a message that names the program three times printed
     * «:app» raw on screen for the other two — measured on hint_legacy_office
     * the day it shipped. split/join rather than a regex: the values are product
     * names, and `$&`/`$1` in a replacement string are live syntax. Longest key
     * first so one placeholder can never eat another's prefix. */
    if (repl) {
        for (const r of Object.keys(repl).sort((a, b) => b.length - a.length)) {
            s = s.split(':' + r).join(String(repl[r]));
        }
    }
    return s;
};

/* Families that appear in the "convert all" bar, in display order. */
const FAMILIES = ['audio', 'video', 'image', 'pdf', 'archive', 'subtitle', 'data', 'office', 'font'];

/* Where a refusal is actually a referral. code → tool slug (URL from CFG.routes).
 * DECODE_FAILED and PDF_PARSE_FAILED belong here for the same reason
 * SOURCE_CORRUPT does: a file whose pixels or page tree will not parse is a
 * damaged file, and the FAQ on this very page promises the doctor for it. They
 * were the only "the bytes are broken" codes that dead-ended in a red line. */
const RESCUE_ROUTES = {
    SOURCE_CORRUPT: 'file-doctor',
    DECODE_FAILED: 'file-doctor',
    PDF_PARSE_FAILED: 'file-doctor',
    PDF_NO_TEXT_LAYER: 'ocr-text-extractor',
    PDF_PASSWORD: 'unlock-pdf',
    // A RAW whose embedded previews are all gone still has the photograph in
    // its sensor stream — /raw-rescue is the tool that develops it. Sending
    // the person there is the answer; a generic "decode failed" is not.
    RAW_NO_PREVIEW: 'raw-rescue',
};

const ENGINES = {};   // name -> module promise (lazy, one import per family)
function engine(name) {
    if (!ENGINES[name]) ENGINES[name] = import('./engines/' + name + '.js');
    return ENGINES[name];
}

/* ------------------------------------------------------------------ state */

let items = [];        // [{ id, file, det, targets, targetId, status, blob(s), error }]
let seq = 0;
let running = false;
let cancelled = false;
let ffPressure = { bytes: 0, ops: 0 };

const $ = id => document.getElementById(id);
const el = (tag, cls, html) => {
    const n = document.createElement(tag);
    if (cls) n.className = cls;
    if (html != null) n.innerHTML = html;
    return n;
};
const esc = s => String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

function fmtSize(b) {
    if (b >= 1073741824) return (b / 1073741824).toFixed(2) + ' ' + t('unit_gb');
    if (b >= 1048576) return (b / 1048576).toFixed(1) + ' ' + t('unit_mb');
    if (b >= 1024) return Math.round(b / 1024) + ' ' + t('unit_kb');
    return b + ' ' + t('unit_b');
}
function baseName(name) {
    const i = String(name || 'file').lastIndexOf('.');
    return i > 0 ? name.slice(0, i) : (name || 'file');
}
function stage(name) { try { window.__stage && window.__stage(name); } catch (_) {} }
function feature(f, data) {
    try { window.__ae && window.__ae('feature_use', Object.assign({ slug: SLUG, f }, data || {})); } catch (_) {}
}

/* ------------------------------------------------------------------ intake */

/* ── Office lane (public/js/office-lane.js, Фарид 16.10.2026) ─────────────
 * Word/PowerPoint/Excel 97–2003, OpenDocument templates and drawings, iWork,
 * WordPerfect, Works, Publisher, Visio: the browser cannot read them, so this
 * converter had nothing honest to offer — a «no conversions» shrug with a
 * «save it as .docx» hint, or worse, a 7Z/TAR re-pack of a Pages document
 * (detected as a plain ZIP). LibreOffice on our server converts them for
 * Premium; the door under the row says that, why, and the way without it.
 * Only where the converter had no real conversion: ODT → PDF, ODS → tables
 * and the like keep their browser lanes. The config element exists only while
 * the lane is on (partials/office-lane-config) — off, the old hints stand. */
const OFFICE_EXT = new Set(['doc', 'dot', 'ppt', 'pps', 'pot', 'xls', 'xlt', 'odt', 'ott', 'odp', 'otp',
    'ods', 'ots', 'odg', 'otg', 'pages', 'key', 'numbers', 'wpd', 'wps', 'pub', 'vsd', 'vsdx']);
let olLoad = null;
function officeLane() {
    if (!document.getElementById('officeLaneCfg')) return Promise.resolve(null);
    if (window.OfficeLane) return Promise.resolve(window.OfficeLane);
    if (!olLoad) {
        olLoad = import('../office-lane.js').then(() => window.OfficeLane || null, () => { olLoad = null; return null; });
    }
    return olLoad;
}
async function officeVerdict(it) {
    const det = it.det || {};
    if (!(det.family === 'compound' || det.family === 'archive' || OFFICE_EXT.has(det.nameExt) || OFFICE_EXT.has(det.format))) return null;
    // A real browser conversion stays; a shrug, a rename or a re-pack of a document does not count as one.
    const noReal = !it.targets.length || det.family === 'archive' || it.targets.every(x => x.rename);
    if (!noReal) return null;
    const OL = await officeLane();
    if (!OL || !OL.available()) return null;
    const info = await OL.classify(it.file);
    return info.status === 'legacy' && info.family ? { OL, info } : null;
}
function mountOfficeDoor(it, OL) {
    const row = $('fcRow' + it.id);
    if (!row || !it.office || it.office.encrypted) return;
    const host = el('div', 'fc-office');
    host.id = 'fcOffice' + it.id;
    row.after(host);
    it.door = OL.door(host, { file: it.file, info: it.office, tool: SLUG, mode: 'download' });
}
function dropOfficeDoor(it) {
    if (it && it.door) { try { it.door.destroy(); } catch (_) {} it.door = null; }
    const host = it && $('fcOffice' + it.id);
    if (host) host.remove();
}

async function addFiles(files) {
    const box = $('fcQueueBox');
    if (box) box.hidden = false;
    stage('file_picked');
    for (const file of files) {
        const it = { id: ++seq, file, det: null, targets: [], targetId: null,
                     status: 'detecting', results: null, error: null };
        items.push(it);
        renderRow(it);
        try {
            it.det = await detectFile(file);
            it.targets = targetsFor(it.det);
            it.hint = hintFor(it.det, CFG.routes);
            it.heavy = heavyForDevice(it.det, file);
            if (it.heavy) {
                feature('heavy_for_device', {
                    fmt: it.det.format, fileMB: Math.round(file.size / 1048576),
                    hintMB: it.heavy.hintMB, share: it.heavy.share,
                });
            }
            if (it.det.format === 'json') {
                it.jk = await jsonKind(file);
                feature('json_kind', { jk: it.jk, kb: Math.round(file.size / 1024) });
            }
            if (it.det.format === 'xlsx') await loadSheetList(it);
            const lo = await officeVerdict(it);
            if (lo) {
                it.office = lo.info;
                it.officeLane = lo.OL;
                if (it.det.family === 'archive') it.targets = [];   // a re-pack of a document is no conversion
            }
            it.targetId = pickDefault(it);
            it.status = it.targets.length ? 'ready' : (it.office ? 'office' : 'no_targets');
            /* `v` (the doctor's verdict) and `ext` (what the NAME says): without
               them an all-zero `.cr2` and a format we lack were one row in the
               log — fmt cr2, fam unknown — and only a session replay told them
               apart. */
            if (!it.targets.length && !it.office) feature('no_targets', { fmt: it.det.format, fam: it.det.family, hint: (it.hint && it.hint.key) || '', v: it.det.verdict || '', ext: it.det.nameExt || '' });
            if (it.det.renamed) feature('renamed_detected', { fmt: it.det.format, ext: it.det.nameExt });
        } catch (e) {
            it.status = 'no_targets';
            it.det = { family: 'unknown', format: 'unknown', label: '?', basis: 'extension' };
        }
        renderRow(it);
        if (it.office && it.officeLane) mountOfficeDoor(it, it.officeLane);
    }
    renderBar();
    precheck();
}

/* ── Workbook sheets (Excel → PDF / Word) ───────────────────────────────
 * «Only the first sheet» and «the whole book whether I want it or not» are the
 * two complaints about every Excel converter. A workbook with two or more
 * sheets gets a row of sheet chips under its row: visible sheets on, hidden
 * ones off (and marked), chart sheets not offered. Reading the list inflates
 * workbook.xml only. */
const TABLE_DOC = { pdf: 1, png: 1, jpg: 1, docx: 1 };
async function loadSheetList(it) {
    try {
        const { sheetList } = await import('./xlsx-read.js');
        const list = (await sheetList(it.file)).filter(s => s.kind === 'worksheet');
        it.sheets = list;
        it.xlSheets = list.filter(s => s.state === 'visible').map(s => s.index);
        if (!it.xlSheets.length && list.length) it.xlSheets = [list[0].index];
    } catch (_) { it.sheets = null; }
}

function sheetPickerHtml(it) {
    if (!it.sheets || it.sheets.length < 2 || !TABLE_DOC[it.targetId] || it.status === 'detecting') return '';
    const on = new Set(it.xlSheets || []);
    let html = '<div class="fc-xlsheets" role="group" aria-label="' + esc(t('xl_sheets_label')) + '"><span class="fc-xlsheets-lbl">' + esc(t('xl_sheets_label')) + '</span>';
    for (const s of it.sheets) {
        html += '<label class="fc-xlsheet' + (s.state !== 'visible' ? ' fc-xlsheet-hidden' : '') + '"><input type="checkbox" class="fc-xlsheet-cb" data-id="' + it.id + '" data-sheet="' + s.index + '"' + (on.has(s.index) ? ' checked' : '') + '>' +
            '<span>' + esc(s.name) + (s.state !== 'visible' ? ' · ' + esc(t('xl_sheet_hidden')) : '') + '</span></label>';
    }
    return html + '</div>';
}

/* The picker follows the target without re-rendering the row (the open
 * <select> keeps its focus). */
function syncSheetPicker(it) {
    const row = $('fcRow' + it.id);
    if (!row) return;
    const old = row.querySelector('.fc-xlsheets');
    const html = sheetPickerHtml(it);
    if (old) old.remove();
    if (html) row.insertAdjacentHTML('beforeend', html);
}

/* Deep-link preset: /file-converter?to=mp3 (optionally &from=mp4) preselects
 * the target for matching files. This is the shareable-link mechanism AND
 * what the curated pair pages feed (they render this page with the preset). */
const PRESET = (() => {
    try {
        if (window.__FC_PRESET) {
            return { from: String(window.__FC_PRESET.from || '').toLowerCase(),
                     to: String(window.__FC_PRESET.to || '').toLowerCase() };
        }
        const q = new URLSearchParams(location.search);
        return { from: (q.get('from') || '').toLowerCase(), to: (q.get('to') || '').toLowerCase() };
    } catch (_) { return { from: '', to: '' }; }
})();

function pickDefault(it) {
    if (PRESET.to && it.targets.some(x => x.id === PRESET.to) &&
        (!PRESET.from || it.det.format === PRESET.from || it.det.ext === PRESET.from)) {
        return PRESET.to;
    }
    /* If a family-wide "all to X" is already chosen, a newly added file of
       that family follows it — mixed-batch users pick once, not N times. */
    const fam = it.det.family;
    const sel = $('fcAll_' + fam);
    if (sel && sel.value && it.targets.some(x => x.id === sel.value)) return sel.value;
    const def = defaultTargetId(it.det);
    return it.targets.some(x => x.id === def) ? def : (it.targets[0] && it.targets[0].id);
}

/* ------------------------------------------------------------------ render */

/* The tile says WHAT the file is. It carries the detected extension, not the
 * name's — so a .jpg holding WebP bytes shows WEBP, and the warning chip below
 * explains why. Long ids get the smaller step so "MICRODVD" still fits. */
function tileFor(it) {
    const d = it.det || {};
    const txt = String(d.ext || d.format || '?').toUpperCase().slice(0, 8);
    const cls = txt.length <= 3 ? 'fc-tile fc-tile-lg' : 'fc-tile';
    return '<div class="' + cls + '" aria-hidden="true">' + esc(txt) + '</div>';
}

/* Chips are for ANOMALIES only. The format itself moved to the tile; repeating
 * it here bought a grid column to say the same word twice. */
function chipFor(it) {
    const d = it.det || {};
    /* RAW is converted from the JPEG the camera embedded, not by developing the
     * sensor data — and the two are not always the same picture: measured on
     * real bodies, Canon 7D and Nikon D3200 embed the FULL frame while a Sony
     * NEX-6 embeds 1616×1080 of a 4912×3264 shot. Saying so up front is the
     * difference between a known limit and a silent downgrade. */
    if (isRaw(d.format) || isRaw(d.nameExt)) {
        return '<span class="fc-chip fc-chip-dim" title="' + esc(t('raw_hint')) + '">' +
            esc(t('raw_chip')) + '</span>';
    }
    if (d.renamed && d.nameExt) {
        return '<span class="fc-chip fc-chip-warn" title="' + esc(t('renamed_hint')) + '">' +
            esc(t('renamed_chip', { ext: d.nameExt.toUpperCase() })) + '</span>';
    }
    if (d.basis === 'extension') {
        return '<span class="fc-chip fc-chip-dim">' + esc(t('by_extension')) + '</span>';
    }
    return '';
}

/* Is this file heavy for THIS device — measured, not guessed?
 *
 * Prod, 90 days, /file-converter completion against the uploader's own device
 * hint (`__effectiveFileLimit`, the 250 MB-per-GB-of-RAM formula):
 *
 *     under 25 % of the hint   54 %   (378 sessions, median 13 MB)
 *     25–50 %                  42 %   (12 sessions, avg 333 MB)
 *     50–100 %                 38 %   (8 sessions, avg 647 MB)
 *     over the hint            23 %   (22 sessions, avg 4586 MB)
 *
 * The curve bends at a QUARTER of the hint, not at it — so the hint is roughly
 * 4× too generous for this kind of work, and the bench says why: a video
 * conversion peaks at 2.5–4.6× the file's own size in real process RSS
 * (tests/playwright/file-converter-big-video-bench.mjs). A 1 GB file wants
 * 3–5 GB, and the hint on a 4 GB machine is 1 GB.
 *
 * ⚠\uFE0F This does NOT touch `__effectiveFileLimit` itself. That formula is shared
 * by every tool with max-mb="auto"; re-deriving it from one tool's traffic
 * would be changing hundreds of pages on this evidence. The finding is
 * reported; the local note is what this measurement licenses.
 *
 * ⚠\uFE0F VIDEO only. The cohort that fails is video (mkv 5 sessions, mts 3, mov 2,
 * avi 2, mp4 4); a 400 MB PDF rasterises page by page and never holds the
 * document in memory, so the same size means nothing there.
 *
 * A note, not a wall: 38–42 % of these DO finish, and refusing them would take
 * away a conversion that works.
 */
function heavyForDevice(det, file) {
    if (!det || det.family !== 'video' || !file || !file.size) return null;
    if (typeof window.__effectiveFileLimit !== 'function') return null;
    let hintMB = 0;
    try { hintMB = Math.round(window.__effectiveFileLimit('video') / 1048576); } catch (_) { return null; }
    if (!hintMB) return null;
    const fileMB = file.size / 1048576;
    const share = fileMB / hintMB;
    if (share < 0.25) return null;
    return { hintMB, fileMB: Math.round(fileMB), share: Math.round(share * 100) / 100 };
}

function targetSelect(it) {
    /* «No conversions for this format» blames the format. For a file with no
     * content (all zeros, zero bytes) the format is not the problem, and the
     * hint in the status cell says what is — the target cell stays blank. */
    if (!it.targets.length && ((it.hint && it.hint.noFormat) || it.office)) return '<span class="fc-noconv">—</span>';
    if (!it.targets.length) return '<span class="fc-noconv">' + esc(t('no_targets')) + '</span>';
    let html = '<select class="fc-target fc-select" data-id="' + it.id + '" aria-label="' + esc(t('target_aria')) + '">';
    let group = null;
    for (const x of it.targets) {
        if (x.group !== group) {
            if (group !== null) html += '</optgroup>';
            group = x.group;
            html += '<optgroup label="' + esc(t('group_' + group)) + '">';
        }
        /* A target whose honest limit is part of the choice says so HERE, in
         * the option the person is reading, not in a FAQ below the fold: a
         * deck of page images is not editable text, and a JPEG renamed to .jpg
         * is not a re-encode. Same slot the server badge already used. */
        const aside = x.badge === 'server' ? t('server_badge') : (x.note ? t(x.note) : '');
        html += '<option value="' + esc(x.id) + '"' + (x.id === it.targetId ? ' selected' : '') + '>' +
            esc(x.label) + (aside ? ' — ' + esc(aside) : '') + '</option>';
    }
    if (group !== null) html += '</optgroup>';
    html += '</select>';
    return html;
}

/* A hint that names a place to go. CFG.routes holds only tools that are public
 * in this locale, so a hint pointing at a hidden tool simply has no link. */
function hintRouteHtml(it) {
    const h = it.hint;
    if (!h || !h.route || !CFG.routes || !CFG.routes[h.route]) return '';
    const cls = h.handoff ? 'fc-rescue fc-handoff' : 'fc-rescue';
    const carry = h.handoff ? ' data-id="' + it.id + '" data-key="' + esc(h.handoff) + '"' : '';
    // `also` — ещё маршруты той же подсказки (тот же перенос: файл возьмёт тот тул, что открылся)
    return [h.route].concat(h.also || []).filter(r => CFG.routes[r]).map(r =>
        ' <a class="' + cls + '"' + carry + ' href="' + esc(CFG.routes[r]) + '">' +
        esc(t('route_' + r.replace(/-/g, '_'))) + ' →</a>').join('');
}

/* The link of a `handoff` hint takes the file with it, so the next tool opens
 * with the book already loaded instead of asking for it again. The carry is
 * best-effort (≤200 MB, private mode may refuse): whatever happens, the link
 * still navigates within 2.5 s. A modified click (new tab) is left to the
 * browser — the carry is written all the same and waits 30 min. */
function followHandoff(ev, a) {
    const it = items.find(i => i.id === +a.dataset.id);
    const put = it && typeof window.__toolHandoffPut === 'function'
        ? window.__toolHandoffPut(a.dataset.key, it.file) : Promise.resolve(false);
    if (ev.ctrlKey || ev.metaKey || ev.shiftKey || ev.altKey || ev.button > 0) return;
    ev.preventDefault();
    let gone = false;
    const go = (ok) => {
        if (gone) return;
        gone = true;
        feature('handoff_out', { to: a.dataset.key, ok: ok ? 1 : 0 });
        location.href = a.href;
    };
    put.then(go, () => go(false));
    setTimeout(() => go(false), 2500);
}

function statusHtml(it) {
    switch (it.status) {
        case 'detecting': return '<span class="fc-st">' + esc(t('st_detecting')) + '</span>';
        /* The one thing worth saying about a file we CAN convert: that this
         * one is big enough for the browser to lose. Said here, before the
         * wait, because when it does fail nothing is left running to say it —
         * the tab is gone. */
        case 'ready': return (it.heavy
            ? '<span class="fc-hint">' + esc(t('note_heavy_for_device')) + '</span>'
            : '') + (it.hint && it.hint.withTargets
            // подсказка, которая дополняет готовые строки, а не заменяет «нет конвертаций»
            ? ' <span class="fc-hint">' + esc(t(it.hint.key, it.hint.repl)) + '</span>' + hintRouteHtml(it)
            : '');
        case 'waiting': return '<span class="fc-st">' + esc(t('st_waiting')) + '</span>';
        case 'converting': {
            const pct = Math.round((it.progress || 0) * 100);
            return '<div class="fc-prog" role="progressbar" aria-valuemin="0" aria-valuemax="100" ' +
                'aria-valuenow="' + pct + '"><div class="fc-prog-fill" style="width:' + pct + '%"></div></div>' +
                '<span class="fc-pct">' + pct + '%</span>';
        }
        case 'done': {
            const n = it.results.length;
            const size = it.results.reduce((s, r) => s + r.blob.size, 0);
            // Size delta: the one number a converter user checks after "did it
            // work" — is the result lighter or heavier than what went in.
            let delta = '';
            if (it.file.size > 0 && size > 0) {
                const pct = Math.round((size - it.file.size) / it.file.size * 100);
                if (Math.abs(pct) >= 3) {
                    delta = '<span class="fc-delta">' + (pct > 0 ? '+' : '−') + Math.abs(pct) + '%</span>';
                }
            }
            const notes = (it.notes || []).map(x =>
                '<span class="fc-note">' + esc(t(x.key, { n: x.n })) + '</span>').join('');
            return '<span class="fc-st fc-st-ok">✓ ' + esc(fmtSize(size)) + '</span>' + delta +
                '<button type="button" class="fc-btn fc-btn-strong fc-dl" data-id="' + it.id + '">' +
                esc(n > 1 ? t('download_n', { n }) : t('download')) + '</button>' + notes;
        }
        case 'error': {
            let html = '<span class="fc-st fc-st-err" title="' + esc(it.error.detail || '') + '">' +
                esc(it.error.msg) + '</span>';
            if (it.error.route && CFG.routes && CFG.routes[it.error.route]) {
                html += ' <a class="fc-rescue" href="' + esc(CFG.routes[it.error.route]) + '">' +
                    esc(t('route_' + it.error.route.replace(/-/g, '_'))) + ' →</a>';
            }
            return html;
        }
        case 'no_targets': {
            // A hint, when there is one, replaces the shrug: for a detached
            // signature "нет конвертаций" is true but useless, and the reason
            // is the whole answer.
            const hint = it.hint;
            /* The target cell already says «no conversions» (fc-noconv above),
             * so repeating it here and THEN explaining made the row read the
             * same sentence twice before getting to the point. When there is an
             * explanation it replaces the shrug rather than following it. */
            let html = (hint ? '' : '<span class="fc-st">' + esc(t('no_targets')) + '</span>') +
                (hint ? '<span class="fc-hint">' + esc(t(hint.key, hint.repl)) + '</span>' : '') +
                hintRouteHtml(it);
            /* «Suggest a format» only where a format request could change the
             * answer. A file of zeros has no format to add, and the button made
             * the queue read «CR2 → JPG» for a pair the converter already has. */
            if (!(hint && hint.noSuggest)) {
                html += ' <button type="button" class="fc-suggest-link" data-fmt="' +
                    esc((it.det && it.det.format) || '') + '" data-id="' + it.id + '">' +
                    esc(t('suggest_link')) + '</button>';
            }
            return html;
        }
        case 'office': {
            const OL = window.OfficeLane;
            const say = OL ? OL.text(it.office && it.office.encrypted ? 'err_encrypted' : 'panel_below') : '';
            return '<span class="fc-hint">' + esc(say) + '</span>';
        }
        case 'cancelled': return '<span class="fc-st">' + esc(t('st_cancelled')) + '</span>';
    }
    return '';
}

function renderRow(it) {
    let row = $('fcRow' + it.id);
    if (!row) {
        row = el('div', 'fc-row');
        row.id = 'fcRow' + it.id;
        $('fcQueue').appendChild(row);
    }
    row.setAttribute('data-fam', (it.det && it.det.family) || 'unknown');
    row.innerHTML =
        tileFor(it) +
        '<div class="fc-cell fc-name" title="' + esc(it.file.name) + '">' +
            '<span class="fc-fname">' + esc(it.file.name) + '</span>' +
            '<span class="fc-meta"><span class="fc-sr">' + esc((it.det && it.det.label) || '') + '</span>' +
            '<span>' + esc(fmtSize(it.file.size)) + '</span>' +
            chipFor(it) + '</span></div>' +
        '<div class="fc-cell fc-tgt"><span class="fc-arrow" aria-hidden="true">→</span>' +
            targetSelect(it) + '</div>' +
        '<div class="fc-cell fc-status">' + statusHtml(it) + '</div>' +
        '<button type="button" class="fc-rm" data-id="' + it.id + '" aria-label="' + esc(t('remove')) + '">×</button>' +
        sheetPickerHtml(it);
}

function renderBar() {
    // Хоть одна выбранная цель уходит на сервер (badge 'server' — 5 аудиокодеков
    // без WASM) → блок лимита показывает строку про серверную квоту; сплошь
    // браузерные цели её убирают. renderBar() бежит на каждом add/remove/change,
    // так что одна точка покрывает и per-file селект, и «всю семью — в X».
    if (window.__toolLimit && window.__toolLimit.serverLaneCase) {
        const anyServer = items.some(it => {
            const t = it.targets && it.targets.find(x => x.id === it.targetId);
            return t && t.badge === 'server';
        });
        window.__toolLimit.serverLaneCase(SLUG, anyServer);
    }
    const famBox = $('fcAllBar');
    /* A "convert ALL images to X" pill needs at least two images to be worth
       anything — on one file it duplicates that file's own dropdown. The old
       rule (render for every family present) turned the common mixed drop —
       one of each kind — into a row of seven controls that each governed a
       single row directly above them. */
    const present = FAMILIES.filter(f =>
        items.filter(i => i.det && i.det.family === f && i.targets.length).length >= 2);
    // Selections must survive the rebuild: renderBar() runs on every add,
    // remove and conversion, and a wiped <select> silently unlearns the
    // choice that newly added files of that family follow (pickDefault).
    const keep = {};
    famBox.querySelectorAll('.fc-all-sel').forEach(s => { keep[s.dataset.fam] = s.value; });
    famBox.innerHTML = '';
    for (const fam of present) {
        /* union of targets across the family's files — only offer what EVERY
           file of the family can become, otherwise "all to X" half-applies */
        const famItems = items.filter(i => i.det && i.det.family === fam && i.targets.length);
        let common = famItems[0].targets.map(x => x.id);
        for (const it of famItems.slice(1)) {
            const ids = new Set(it.targets.map(x => x.id));
            common = common.filter(id => ids.has(id));
        }
        if (!common.length) continue;
        const wrap = el('label', 'fc-all');
        wrap.setAttribute('data-fam', fam);
        const labels = {};
        famItems[0].targets.forEach(x => { labels[x.id] = x.label; });
        const prev = keep[fam] && common.indexOf(keep[fam]) >= 0 ? keep[fam] : '';
        wrap.innerHTML = '<span class="fc-all-lbl">' + esc(t('all_' + fam)) +
            ' <span class="fc-all-n">' + famItems.length + '</span></span>' +
            '<span class="fc-arrow" aria-hidden="true">→</span>' +
            '<select class="fc-select fc-all-sel" id="fcAll_' + fam + '" data-fam="' + fam + '">' +
            '<option value="">—</option>' +
            common.map(id => '<option value="' + esc(id) + '"' + (id === prev ? ' selected' : '') + '>' +
                esc(labels[id] || id.toUpperCase()) + '</option>').join('') +
            '</select>';
        famBox.appendChild(wrap);
    }
    const readyN = items.filter(i => i.status === 'ready' || i.status === 'waiting').length;
    $('fcCount').textContent = String(items.length);
    const btn = $('fcConvertBtn');
    btn.disabled = running || readyN === 0;
    // The count on the button is the answer to "what exactly will this do" —
    // after a partial run it is the REMAINING files, not the whole queue.
    $('fcBtnCount').textContent = readyN > 0 ? ' · ' + readyN : '';
    const doneItems = allResults();
    $('fcDownloadAllBtn').hidden = doneItems.length < 1;
    $('fcZipBtn').hidden = doneItems.length < 2;
    $('fcFolderBtn').hidden = doneItems.length < 2 || typeof window.showDirectoryPicker !== 'function';
    $('fcActions').hidden = items.length === 0;
    // The result buttons carry the accent outline once there is something to
    // take away; before that the only accent on the page is "Convert".
    $('fcDownloadAllBtn').classList.toggle('fc-btn-strong', doneItems.length > 0);
    $('fcZipBtn').classList.toggle('fc-btn-strong', doneItems.length > 1);
    /* Nothing left to convert but results on the table: hand the filled
       accent to the download action and let Convert step back — a disabled
       button has no business being the loudest thing on screen. */
    const spent = !running && readyN === 0 && doneItems.length > 0;
    btn.classList.toggle('fc-demoted', spent);
    const lead = doneItems.length > 1 ? $('fcZipBtn') : $('fcDownloadAllBtn');
    for (const b of [$('fcZipBtn'), $('fcDownloadAllBtn')]) b.classList.remove('fc-lead');
    if (spent) { lead.classList.add('fc-lead'); lead.classList.remove('fc-btn-strong'); }
    syncOpts();
    renderSummary();
}

/* Which of the shared option controls can change a byte of the CURRENT queue.
 * Relevance follows the chosen TARGETS, not the sources: an MP4 whose target
 * is MP3 needs the audio bitrate and has no use for a video resolution. */
function syncOpts() {
    const engines = new Set();
    for (const it of items) {
        if (!it.targetId) continue;
        const tg = it.targets.find(x => x.id === it.targetId);
        if (tg) engines.add(tg.engine);
    }
    /* A block appears only when the queue holds a file it can change. The
       image block covers pdf→image too, because that output IS an image. */
    const tableDoc = items.some(it => it.targetId && TABLE_DOC[it.targetId] && it.det &&
        (it.det.format === 'xlsx' || it.det.format === 'ods' || it.det.format === 'csv' || it.det.format === 'tsv' || it.det.format === 'sav' || it.det.format === 'zsav'));
    const w2x = items.some(it => it.targetId === 'xlsx' && it.det && it.det.format === 'docx');
    const need = {
        table: tableDoc,
        w2x,
        image: engines.has('image') || engines.has('pdf'),
        video: engines.has('video') && !targetOnlyAudioVideo(),
        audio: engines.has('audio'),
        pdf: engines.has('pdf'),
    };
    let any = false;
    for (const k in need) {
        const grp = document.querySelector('.fc-optgrp[data-grp="' + k + '"]');
        if (!grp) continue;
        grp.hidden = !need[k];
        if (need[k]) any = true;
    }
    const box = $('fcOpts');
    if (box) box.hidden = !any;
}

/* True when every video-engine job is really an audio extraction (mp4 → mp3):
 * the resolution control would then be a knob wired to nothing. */
function targetOnlyAudioVideo() {
    const vids = items.filter(it => {
        const tg = it.targets.find(x => x.id === it.targetId);
        return tg && tg.engine === 'video';
    });
    return vids.length > 0 && vids.every(it => {
        const tg = it.targets.find(x => x.id === it.targetId);
        return tg && tg.group === 'audio';
    });
}

/* One line under the buttons once a run has ended: how many landed, how much
 * they weigh, how many failed. Reading that off eight separate rows is work
 * the page should have done. */
function renderSummary() {
    const box = $('fcSummary');
    if (!box) return;
    const done = items.filter(i => i.status === 'done');
    const failed = items.filter(i => i.status === 'error').length;
    if (running || (!done.length && !failed)) { box.hidden = true; box.textContent = ''; return; }
    const bytes = done.reduce((s, i) => s + i.results.reduce((a, r) => a + r.blob.size, 0), 0);
    const files = done.reduce((s, i) => s + i.results.length, 0);
    let html = '';
    if (done.length) {
        html += '<b>' + esc(t('summary_done', { n: files })) + '</b> <span>· ' + esc(fmtSize(bytes)) + '</span>';
    }
    if (failed) {
        html += (html ? ' ' : '') + '<span class="fc-summary-err">' +
            esc(t('summary_failed', { n: failed })) + '</span>';
    }
    box.innerHTML = html;
    box.hidden = !html;
}

function allResults() {
    const out = [];
    for (const it of items) if (it.status === 'done') for (const r of it.results) out.push(r);
    return out;
}

/* Batch progress. `done` + the running file's own fraction, so a single large
 * video still moves the bar instead of freezing it at 0 % for two minutes.
 * setOverall(null) hides the row. */
function setOverall(done, total, frac) {
    const box = $('fcOverall');
    if (!box) return;
    if (done == null || !total) { box.hidden = true; return; }
    const pct = Math.max(0, Math.min(100, Math.round((done + (frac || 0)) / total * 100)));
    box.hidden = false;
    $('fcOverallFill').style.width = pct + '%';
    $('fcOverallBar').setAttribute('aria-valuenow', String(pct));
    $('fcOverallTxt').textContent = t('overall_progress', { done: Math.min(done + 1, total), total }) +
        ' · ' + pct + '%';
}

/* ------------------------------------------------------------------ limits */

function precheck() {
    try {
        const files = items.filter(i => i.status === 'ready' || i.status === 'waiting').map(i => i.file);
        window.__toolLimit && window.__toolLimit.precheck(SLUG, files);
    } catch (_) {}
}

/* ------------------------------------------------------------------ run */

async function runQueue() {
    if (running) return;
    const todo = items.filter(i => (i.status === 'ready' || i.status === 'error' || i.status === 'cancelled') && i.targetId);
    if (!todo.length) return;
    running = true; cancelled = false;
    $('fcConvertBtn').disabled = true;
    $('fcCancelBtn').hidden = false;
    stage('processing');
    let okFiles = 0, failFiles = 0;
    const t0 = performance.now();

    for (const it of todo) { it.status = 'waiting'; renderRow(it); }
    let finished = 0;
    setOverall(0, todo.length, 0);

    for (const it of todo) {
        if (cancelled) { it.status = 'cancelled'; renderRow(it); continue; }
        const target = it.targets.find(x => x.id === it.targetId);
        if (!target) continue;

        const token = window.__toolLimit
            ? window.__toolLimit.begin(SLUG, window.__toolLimit.fileKey(it.file))
            : { commit() {}, abort() {} };
        if (!token) { it.status = 'ready'; renderRow(it); break; }   // wall shown; stop the rest

        it.status = 'converting'; it.progress = 0; renderRow(it);
        try {
            const mod = await engine(target.engine);
            const res = await mod.convert({
                file: it.file,
                det: it.det,
                target,
                opts: Object.assign(readOpts(target), { xlSheets: it.xlSheets || null, isCancelled: () => cancelled }),
                baseName: baseName(it.file.name),
                onProgress: p => {
                    it.progress = Math.max(0, Math.min(1, p || 0));
                    const row = $('fcRow' + it.id);
                    const bar = row && row.querySelector('.fc-prog-fill');
                    if (bar) bar.style.width = Math.round(it.progress * 100) + '%';
                    const pct = row && row.querySelector('.fc-pct');
                    if (pct) pct.textContent = Math.round(it.progress * 100) + '%';
                    setOverall(finished, todo.length, it.progress);
                },
                serverRunUrl: CFG.runUrl || null,
                csrf: CFG.csrf || '',
            });
            if (cancelled) { token.abort(); it.status = 'cancelled'; renderRow(it); continue; }
            it.results = (res && res.items) || [];
            // What the engine had to leave out (e.g. an audio track FFmpeg cannot read) — said in the row.
            it.notes = (res && res.notes) || [];
            if (!it.results.length) throw Object.assign(new Error('EMPTY_RESULT'), { code: 'EMPTY_RESULT' });
            it.status = 'done';
            token.commit();
            okFiles++;
            if (res && res.summary) {
                feature('xl_pdf', { sheets: res.summary.length, pages: res.summary.reduce((s, x) => s + x.pages, 0),
                    auto: res.summary.some(x => x.auto) ? 1 : 0, minScale: Math.min.apply(null, res.summary.map(x => x.scale)) });
            }
            feature('converted', {
                from: it.det.format, to: target.id, eng: target.engine,
                inKB: Math.round(it.file.size / 1024),
                outKB: Math.round(it.results.reduce((s, r) => s + r.blob.size, 0) / 1024),
            });
        } catch (e) {
            token.abort();
            if (cancelled) { it.status = 'cancelled'; renderRow(it); continue; }
            failFiles++;
            const code = (e && e.code) || 'CONVERT_FAILED';
            it.error = {
                msg: (e && e.userMessage) || T['err_' + code.toLowerCase()] || t('err_convert_failed'),
                detail: (e && e.message) || '',
                route: RESCUE_ROUTES[code] || null,
            };
            it.status = 'error';
            try {
                window.__aeDiagnostic && window.__aeDiagnostic({
                    issue: 'convert_failed', code,
                    from: it.det && it.det.format, to: target.id,
                    err: String((e && e.message) || '').slice(0, 180),
                    size: it.file.size,
                });
            } catch (_) {}
        }
        finished++;
        setOverall(finished, todo.length, 0);
        renderRow(it);
        renderBar();

        /* Relieve WASM arena pressure between heavy jobs, not after each one:
           maybeReinit() only acts when accumulated bytes/ops are real. */
        if (target.engine === 'audio' || target.engine === 'video') {
            ffPressure.bytes += it.file.size; ffPressure.ops += 1;
            try {
                if (window.FFmpegProcessor &&
                    await window.FFmpegProcessor.maybeReinit(ffPressure, {})) {
                    ffPressure = { bytes: 0, ops: 0 };
                }
            } catch (_) {}
        }
    }

    running = false;
    $('fcCancelBtn').hidden = true;
    setOverall(null);
    renderBar();
    if (okFiles) {
        stage('done');
        try {
            window.__aeToolComplete && window.__aeToolComplete({
                files: okFiles, failed: failFiles,
                secs: Math.round((performance.now() - t0) / 1000),
            });
        } catch (_) {}
        window.__announce && window.__announce(t('done_announce', { n: okFiles }));
    }
}

function readOpts(target) {
    const val = (id, dflt) => ($(id) ? $(id).value : dflt);
    /* Each lane reads ITS OWN control now. The single shared `quality` select
       used to feed three unrelated numbers — a canvas encoder argument, an
       FFmpeg CRF rung and a PDF raster density — so changing it for a photo
       silently re-rendered a PDF in the same batch. */
    const imgTier = val('fcQuality', 'medium');
    /* 'same' — не «нет выбора», а сам выбор: план читает его как «картинку не
       трогать» и разрешает копирование потоков (см. комментарий у #fcVQuality). */
    const vidTier = val('fcVQuality', 'same');
    return {
        // string tier — video.js switches CRF on it
        quality: vidTier,
        bitrate: val('fcBitrate', '') ? parseInt(val('fcBitrate', ''), 10) : undefined,
        resolution: val('fcRes', 'same'),
        // numeric 0..1 — canvas.toBlob() in image.js / pdf.js. Kept as its own
        // key because toBlob() SILENTLY IGNORES a non-numeric quality: passing
        // the string tier there looked wired up and encoded at the browser
        // default every time, so the control moved nothing.
        imgQuality: { high: 0.95, medium: 0.92, low: 0.8 }[imgTier] || 0.92,
        // Explicit now, not derived from a tier the user set for something else.
        dpi: parseInt(val('fcDpi', '150'), 10) || 150,
        /* Document → PDF rasterises text to images, so it follows the IMAGE
           tier. Without this it read `quality`, which is now the video CRF
           rung — a hidden video control would have governed how sharply a
           Word file rendered. */
        imgTier,
        pageRange: ($('fcPages') ? $('fcPages').value.trim() : ''),
        xlFit: val('fcXlFit', 'auto'),
        xlOrientation: val('fcXlOrient', 'file'),
        xlPaper: val('fcXlPaper', 'auto'),
        xlGrid: val('fcXlGrid', 'file'),
        w2xMode: val('fcW2xMode', 'sheets'),
    };
}

/* ------------------------------------------------------------------ output */

// A browser hands over ONE download per user interaction. The 350 ms stagger
// that used to live here carried the comment «browsers drop rapid-fire
// downloads silently» — true, and the stagger does not help: measured on real
// Chrome 152, 350 ms apart delivers 1 of 4 and 3 s apart delivers 1 of 4. It is
// a per-gesture budget, not a rate limit. Worse, the extras poison the tab: it
// then drops single downloads on every page of this origin.
// A batch goes out as a folder or as ONE archive — __batchDeliver owns that
// ladder, including the honest refusal when neither can carry it.
async function deliverMany(list, tag) {
    if (!list.length) return;
    if (list.length === 1) { window.__downloadFile(list[0].blob, list[0].name); return; }
    const BD = window.__batchDeliver || (window.__ensureBatchDeliver
        ? await window.__ensureBatchDeliver().catch(() => null) : null);
    if (!BD) { window.__downloadFile(list[0].blob, list[0].name); return; }
    const res = await BD.deliver(list.map(r => ({ name: r.name, blob: r.blob })),
        { slug: 'file-converter', zipName: 'converted-files.zip' });
    feature(tag, { n: list.length, via: res.via, written: res.written });
}

function downloadItem(it) {
    // Not awaited on purpose: the caller is a click handler, and deliverMany
    // reaches the folder picker on its first await.
    return deliverMany(it.results, 'download_item');
}

async function downloadAll() {
    return deliverMany(allResults(), 'download_all');
}

async function zipAll() {
    const res = allResults();
    if (!res.length) return;
    const btn = $('fcZipBtn');
    btn.disabled = true;
    try {
        const blob = await pack(res, (done, total) => {
            btn.textContent = t('zip_progress', { done, total });
        });
        window.__downloadFile(blob, 'converted.zip');
        feature('zip_download', { n: res.length, kb: Math.round(blob.size / 1024) });
    } catch (e) {
        if (e && e.code === 'ZIP_TOO_BIG') {
            // Was `downloadAll()` — N downloads, of which the browser delivers
            // one. The archive is what failed, so the answer is the OTHER lane:
            // a folder writes each file as it goes and holds nothing.
            window.__toast(t('zip_too_big'), 'warning');
            if (window.__batchDeliver && window.__batchDeliver.folderAvailable()) folderAll();
        } else {
            window.__handleError(e, 'fc-zip', { say: t('zip_error') });
        }
    } finally {
        btn.disabled = false;
        btn.textContent = t('zip_all');
        renderBar();
    }
}

async function folderAll() {
    const res = allResults();
    try {
        const n = await saveToFolder(res, (done, total) => {
            $('fcFolderBtn').textContent = t('zip_progress', { done, total });
        });
        if (n != null) {
            window.__toast(t('folder_saved', { n }), 'success');
            feature('folder_save', { n });
        }
    } catch (e) {
        window.__handleError(e, 'fc-folder', { say: t('folder_error') });
    } finally {
        $('fcFolderBtn').textContent = t('save_folder');
    }
}

/* -------------------------------------------------------------- suggestion */

/* The file a suggestion is ABOUT — the row whose «Suggest» was pressed, or,
 * from the general link, the last file added. Its detection travels with the
 * suggestion as one string (`det`), because the telemetry that explains a
 * request lives 20 days and the queue is read later than that: 7 of 17 rows of
 * the 2026-10-15 review had nothing left to read. */
let suggestCtx = null;

function openSuggest(fromFmt, it) {
    suggestCtx = it || null;
    const box = $('fcSuggest');
    box.hidden = false;
    if (fromFmt) $('fcSugFrom').value = fromFmt.toUpperCase();
    box.scrollIntoView({ block: 'nearest' });
    feature('suggest_open', { fmt: fromFmt || '' });
}

function suggestionData(from, to, note) {
    const ctx = suggestCtx || items[items.length - 1] || null;
    const data = { from, to, note };
    const det = evidenceOf(ctx, suggestCtx ? 'row' : 'last', items.length);
    if (det) data.det = det;
    if (ctx && ctx.jk) data.jk = ctx.jk;
    return data;
}

async function sendSuggest() {
    const from = $('fcSugFrom').value.trim();
    const to = $('fcSugTo').value.trim();
    if (!from && !to) return;
    const btn = $('fcSugSend');
    btn.disabled = true;
    try {
        const r = await fetch(CFG.suggestUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': CFG.csrf },
            credentials: 'same-origin',
            body: JSON.stringify({
                tool_slug: SLUG,
                locale: window.__LOCALE || 'en',
                data: suggestionData(from, to, $('fcSugNote').value.trim().slice(0, 500)),
            }),
        });
        if (!r.ok) throw new Error('HTTP ' + r.status);
        $('fcSuggestForm').hidden = true;
        $('fcSuggestOk').hidden = false;
        feature('suggest_sent', { from, to });
    } catch (e) {
        window.__toast(t('suggest_error'), 'error');
        btn.disabled = false;
    }
}

/* ------------------------------------------------------------------ wiring */

function init() {
    const zone = $('fcDrop');
    if (zone) {
        zone.addEventListener('tool-uploader:file', ev => {
            const files = (ev.detail && (ev.detail.files || [ev.detail.file])) || [];
            addFiles(Array.from(files).filter(Boolean));
        });
    }

    document.addEventListener('change', ev => {
        const sel = ev.target;
        if (sel.classList && sel.classList.contains('fc-xlsheet-cb')) {
            const it = items.find(i => i.id === +sel.dataset.id);
            if (it) {
                const k = +sel.dataset.sheet;
                const set = new Set(it.xlSheets || []);
                if (sel.checked) set.add(k); else set.delete(k);
                // at least one sheet stays chosen — an empty choice is no conversion
                if (!set.size) { set.add(k); sel.checked = true; }
                it.xlSheets = it.sheets.map(s => s.index).filter(x => set.has(x));
                if (it.status === 'done' || it.status === 'error') { it.status = 'ready'; it.results = null; it.notes = null; renderRow(it); renderBar(); }
                feature('xl_sheets', { n: it.xlSheets.length, of: it.sheets.length });
            }
            return;
        }
        if (sel.classList && sel.classList.contains('fc-target')) {
            const it = items.find(i => i.id === +sel.dataset.id);
            if (it) {
                it.targetId = sel.value;
                if (it.status === 'done' || it.status === 'error') { it.status = 'ready'; it.results = null; it.notes = null; renderRow(it); }
                else syncSheetPicker(it);
                // Always: which option controls are relevant follows the chosen
                // targets, so picking MP3 for an MP4 must reveal the bitrate.
                renderBar();
            }
        }
        if (sel.classList && sel.classList.contains('fc-all-sel') && sel.value) {
            const fam = sel.dataset.fam;
            for (const it of items) {
                if (it.det && it.det.family === fam && it.targets.some(x => x.id === sel.value)) {
                    it.targetId = sel.value;
                    if (it.status === 'done' || it.status === 'error') { it.status = 'ready'; it.results = null; it.notes = null; }
                    renderRow(it);
                }
            }
            renderBar();
            feature('all_to', { fam, to: sel.value });
        }
    });

    document.addEventListener('click', ev => {
        const b = ev.target.closest ? ev.target.closest('button,a') : null;
        if (!b) return;
        if (b.classList.contains('fc-rm')) {
            const id = +b.dataset.id;
            dropOfficeDoor(items.find(i => i.id === id));
            items = items.filter(i => i.id !== id);
            const row = $('fcRow' + id);
            if (row) row.remove();
            renderBar(); precheck();
        } else if (b.classList.contains('fc-dl')) {
            const it = items.find(i => i.id === +b.dataset.id);
            if (it && it.results) downloadItem(it);
        } else if (b.classList.contains('fc-suggest-link')) {
            openSuggest(b.dataset.fmt, items.find(i => i.id === +b.dataset.id));
        } else if (b.classList.contains('fc-handoff')) {
            followHandoff(ev, b);
        }
    });

    $('fcConvertBtn').addEventListener('click', () => { runQueue(); });
    $('fcCancelBtn').addEventListener('click', () => {
        cancelled = true;
        try { window.FFmpegProcessor && window.FFmpegProcessor.abort(); } catch (_) {}
        feature('cancel', {});
    });
    $('fcClearBtn').addEventListener('click', () => {
        if (running) return;
        items.forEach(dropOfficeDoor);
        items = [];
        $('fcQueue').innerHTML = '';
        $('fcQueueBox').hidden = true;
        renderBar();
        try { window.__toolLimit && window.__toolLimit.dismiss && window.__toolLimit.dismiss(SLUG); } catch (_) {}
    });
    $('fcDownloadAllBtn').addEventListener('click', downloadAll);
    $('fcZipBtn').addEventListener('click', zipAll);
    $('fcFolderBtn').addEventListener('click', folderAll);
    $('fcSugSend').addEventListener('click', sendSuggest);
    $('fcSuggestOpen').addEventListener('click', () => openSuggest(''));

    /* Remember the export tiers between visits. These are PREFERENCES: someone
     * who always wants 320 kbps wants it next Tuesday too, and re-picking it
     * on every visit is the small tax this tool was charging.
     *
     * ⛔ fcPages is deliberately NOT persisted. A page range belongs to ONE
     * document — restoring "1-5" onto the next PDF would silently hand back
     * five pages of a forty-page file and look like it worked. A setting is
     * only a preference when it is still true for the next file. */
    try {
        if (window.__toolSettings) {
            window.__toolSettings.bind(SLUG, $('fcQuality'), 'quality', 'medium');
            window.__toolSettings.bind(SLUG, $('fcBitrate'), 'bitrate', '');
            window.__toolSettings.bind(SLUG, $('fcRes'), 'resolution', 'same');
            window.__toolSettings.bind(SLUG, $('fcVQuality'), 'vquality', 'same');
            window.__toolSettings.bind(SLUG, $('fcDpi'), 'dpi', '150');
            window.__toolSettings.bind(SLUG, $('fcXlFit'), 'xl_fit', 'auto');
            window.__toolSettings.bind(SLUG, $('fcXlOrient'), 'xl_orient', 'file');
            window.__toolSettings.bind(SLUG, $('fcXlPaper'), 'xl_paper', 'auto');
            window.__toolSettings.bind(SLUG, $('fcXlGrid'), 'xl_grid', 'file');
            window.__toolSettings.bind(SLUG, $('fcW2xMode'), 'w2x_mode', 'sheets');
        }
    } catch (_) {}

    try { window.__toolLimit && window.__toolLimit.mount(SLUG, {}); } catch (_) {}
}

/* Test hook: lets the Playwright matrix read detection + results without
 * intercepting downloads. Returns plain data (first result bytes as hex) so
 * the harness can verify the OUTPUT SIGNATURE, not just that "something"
 * downloaded — a converter that emits a mislabeled container must fail CI. */
window.__fcState = async function () {
    const out = [];
    for (const it of items) {
        const row = {
            id: it.id, name: it.file.name, status: it.status,
            family: it.det && it.det.family, format: it.det && it.det.format,
            renamed: !!(it.det && it.det.renamed),
            targetId: it.targetId,
            error: it.error ? { msg: it.error.msg, detail: it.error.detail } : null,
            results: [],
        };
        if (it.results) {
            for (const r of it.results) {
                const head = new Uint8Array(await r.blob.slice(0, 16).arrayBuffer());
                row.results.push({
                    name: r.name, size: r.blob.size,
                    head: Array.from(head).map(b => b.toString(16).padStart(2, '0')).join(''),
                });
            }
        }
        out.push(row);
    }
    return out;
};

/* Sync twin for waitForFunction polling: Playwright's waitForFunction treats
 * a returned Promise as truthy immediately, so the async __fcState() cannot
 * be polled — this one can. */
window.__fcStatuses = function () {
    return items.map(it => it.status);
};

/* Companion hook: full text of a result blob (for CSV/HTML/VTT assertions). */
window.__fcResultText = async function (itemIdx, resultIdx) {
    const it = items[itemIdx || 0];
    if (!it || !it.results || !it.results[resultIdx || 0]) return null;
    return it.results[resultIdx || 0].blob.text();
};

/* The blob itself, for FIDELITY checks — a signature proves the container,
 * not that the content survived. The fidelity harness decodes the result with
 * the browser's own codecs to assert properties that hold for any input:
 * pixel dimensions unchanged, audio duration unchanged, page not blank. */
window.__fcResultBlob = function (itemIdx, resultIdx) {
    const it = items[itemIdx || 0];
    if (!it || !it.results || !it.results[resultIdx || 0]) return null;
    return it.results[resultIdx || 0].blob;
};

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();

